import { useEffect, useRef, useState } from "react";
import api, { photoUrl } from "@/lib/api";
import { toast } from "sonner";
import { useBilling } from "@/context/BillingContext";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Home,
  KeyRound,
  RefreshCw,
  Trash2,
  Pencil,
  X,
  Camera,
  Clock,
  Loader2,
  DollarSign,
  Image as ImageIcon,
  Upload,
  Sparkles,
} from "lucide-react";

const blank = {
  name: "",
  address: "",
  image_url: "",
  cost_per_cleaning: "",
  cleaner_rate: "",
  color: "",
  checkin_time: "",
  checkout_time: "",
  door_codes: [],
  access_notes: "",
  ical_airbnb: "",
  ical_vrbo: "",
  photo_checklist: [],
  homeowner_name: "",
  homeowner_email: "",
};

const PALETTE = [
  "#4A7C59", "#2563EB", "#DB2777", "#D97706", "#7C3AED", "#0891B2",
  "#DC2626", "#65A30D", "#C026D3", "#0D9488", "#EA580C", "#4F46E5",
  "#0EA5E9", "#14B8A6", "#84CC16", "#EAB308", "#F97316", "#EC4899",
  "#8B5CF6", "#6366F1", "#22C55E", "#E11D48", "#A855F7", "#475569",
];

export function formatTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  if (isNaN(h)) return t;
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

export default function Properties() {
  const [properties, setProperties] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [newCode, setNewCode] = useState({ label: "", code: "" });
  const [newPhoto, setNewPhoto] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { isPremium } = useBilling();
  const [refPhotos, setRefPhotos] = useState([]);
  const [roomLabel, setRoomLabel] = useState("");
  const [refUploading, setRefUploading] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const refFileInput = useRef(null);

  const load = () => api.get("/properties").then((r) => setProperties(r.data));
  useEffect(() => {
    load();
  }, []);

  const loadRefPhotos = (id) =>
    api.get(`/properties/${id}/reference-photos`).then((r) => setRefPhotos(r.data)).catch(() => {});

  const openNew = () => {
    setForm(blank);
    setEditingId(null);
    setRefPhotos([]);
    setRoomLabel("");
    setOpen(true);
  };

  const openEdit = (p) => {
    setForm({
      ...blank,
      ...p,
      cost_per_cleaning: String(p.cost_per_cleaning ?? ""),
      cleaner_rate: String(p.cleaner_rate ?? ""),
    });
    setEditingId(p.id);
    setRefPhotos([]);
    setRoomLabel("");
    if (isPremium) loadRefPhotos(p.id);
    setOpen(true);
  };

  const uploadRefPhoto = async (file) => {
    if (!file || !editingId) return;
    setRefUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("room", roomLabel.trim());
    try {
      await api.post(`/properties/${editingId}/reference-photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Reference photo added");
      await loadRefPhotos(editingId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setRefUploading(false);
    }
  };

  const deleteRefPhoto = async (id) => {
    try {
      await api.delete(`/reference-photos/${id}`);
      setRefPhotos((ps) => ps.filter((p) => p.id !== id));
      toast.success("Reference photo removed");
    } catch (e) {
      toast.error("Could not delete photo");
    }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Property name is required");
    setSaving(true);
    const payload = {
      ...form,
      cost_per_cleaning: parseFloat(form.cost_per_cleaning) || 0,
      cleaner_rate: parseFloat(form.cleaner_rate) || 0,
    };
    try {
      if (editingId) await api.put(`/properties/${editingId}`, payload);
      else await api.post("/properties", payload);
      toast.success(editingId ? "Property updated" : "Property added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Could not save property");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/properties/${id}`);
      toast.success("Property deleted");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error("Could not delete property");
    }
  };

  const sync = async (id) => {
    setSyncing(id);
    try {
      const { data } = await api.post(`/properties/${id}/sync`);
      toast.success(
        `Synced ${data.synced} new reservation(s), ${data.jobs_created} job(s) created`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(null);
    }
  };

  const addCode = () => {
    if (!newCode.label || !newCode.code) return;
    setForm((f) => ({ ...f, door_codes: [...f.door_codes, newCode] }));
    setNewCode({ label: "", code: "" });
  };
  const addPhoto = () => {
    if (!newPhoto.trim()) return;
    setForm((f) => ({ ...f, photo_checklist: [...f.photo_checklist, newPhoto.trim()] }));
    setNewPhoto("");
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs tracking-[0.15em] uppercase font-bold text-primary mb-1">
            Manage
          </p>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Properties</h1>
        </div>
        <Button onClick={openNew} data-testid="add-property-button">
          <Plus className="h-4 w-4 mr-2" /> Add property
        </Button>
      </div>

      {properties.length === 0 ? (
        <Card className="p-12 text-center">
          <Home className="h-10 w-10 text-muted-foreground mx-auto mb-4" strokeWidth={1.3} />
          <p className="text-muted-foreground">
            No properties yet. Add your first rental to get started.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {properties.map((p) => (
            <Card
              key={p.id}
              className="overflow-hidden hover:shadow-md transition-shadow border-l-4"
              style={{ borderLeftColor: p.color || "#4A7C59" }}
              data-testid={`property-card-${p.id}`}
            >
              <div
                className="h-36 bg-cover bg-center bg-muted"
                style={{
                  backgroundImage: `url('${
                    p.image_url ||
                    "https://images.pexels.com/photos/3965509/pexels-photo-3965509.jpeg"
                  }')`,
                }}
              />
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-display text-lg font-semibold truncate flex items-center gap-2">
                      <span
                        className="h-3 w-3 rounded-full shrink-0"
                        style={{ backgroundColor: p.color || "#4A7C59" }}
                      />
                      {p.name}
                    </h3>
                    <p className="text-sm text-muted-foreground truncate">
                      {p.address || "No address"}
                    </p>
                  </div>
                  <Badge variant="secondary" className="shrink-0 gap-1">
                    <DollarSign className="h-3 w-3" />
                    {p.cost_per_cleaning}
                  </Badge>
                </div>

                <div className="flex flex-wrap gap-2 mt-3 text-xs">
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    Charge <b className="text-foreground">${p.cost_per_cleaning}</b>
                  </span>
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    Cleaner payout <b className="text-primary">${p.cleaner_rate ?? 0}</b>
                  </span>
                </div>

                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-4 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <KeyRound className="h-3.5 w-3.5" /> {p.door_codes?.length || 0} codes
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Camera className="h-3.5 w-3.5" /> {p.photo_checklist?.length || 0} photo tasks
                  </span>
                  {(p.checkout_time || p.checkin_time) && (
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      {p.checkout_time ? `out ${formatTime(p.checkout_time)}` : ""}
                      {p.checkout_time && p.checkin_time ? " · " : ""}
                      {p.checkin_time ? `in ${formatTime(p.checkin_time)}` : ""}
                    </span>
                  )}
                </div>

                <div className="flex gap-2 mt-5">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="flex-1"
                    onClick={() => sync(p.id)}
                    disabled={syncing === p.id || (!p.ical_airbnb && !p.ical_vrbo)}
                    data-testid={`sync-property-${p.id}`}
                  >
                    {syncing === p.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    <span className="ml-1">Sync</span>
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`edit-property-${p.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleteTarget(p)}
                    data-testid={`delete-property-${p.id}`}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto scrollbar-thin">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editingId ? "Edit property" : "Add property"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Property name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Seaside Cottage"
                data-testid="property-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="123 Ocean Ave"
                data-testid="property-address-input"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Home owner name</Label>
                <Input
                  value={form.homeowner_name}
                  onChange={(e) => setForm({ ...form, homeowner_name: e.target.value })}
                  placeholder="Jane Host"
                  data-testid="property-homeowner-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Home owner email</Label>
                <Input
                  type="email"
                  value={form.homeowner_email}
                  onChange={(e) => setForm({ ...form, homeowner_email: e.target.value })}
                  placeholder="owner@email.com"
                  data-testid="property-homeowner-email-input"
                />
                <p className="text-[11px] text-muted-foreground">Used to send invoices</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Charge per cleaning ($)</Label>
                <Input
                  type="number"
                  value={form.cost_per_cleaning}
                  onChange={(e) => setForm({ ...form, cost_per_cleaning: e.target.value })}
                  placeholder="400"
                  data-testid="property-cost-input"
                />
                <p className="text-[11px] text-muted-foreground">What the client pays you (revenue)</p>
              </div>
              <div className="space-y-2">
                <Label>Cleaner payout rate ($)</Label>
                <Input
                  type="number"
                  value={form.cleaner_rate}
                  onChange={(e) => setForm({ ...form, cleaner_rate: e.target.value })}
                  placeholder="150"
                  data-testid="property-cleaner-rate-input"
                />
                <p className="text-[11px] text-muted-foreground">Paid to cleaners, split if multiple</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Cover image URL</Label>
              <Input
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                placeholder="https://..."
              />
            </div>

            <div className="space-y-2">
              <Label>Color label</Label>
              <div className="flex flex-wrap gap-2">
                {PALETTE.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, color: c })}
                    data-testid={`color-${c}`}
                    className={`h-8 w-8 rounded-full transition-transform ${
                      form.color === c ? "ring-2 ring-offset-2 ring-foreground scale-110" : ""
                    }`}
                    style={{ backgroundColor: c }}
                    aria-label={`Color ${c}`}
                  />
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Airbnb iCal link</Label>
              <Input
                value={form.ical_airbnb}
                onChange={(e) => setForm({ ...form, ical_airbnb: e.target.value })}
                placeholder="https://www.airbnb.com/calendar/ical/..."
                data-testid="property-airbnb-input"
              />
            </div>
            <div className="space-y-2">
              <Label>VRBO iCal link</Label>
              <Input
                value={form.ical_vrbo}
                onChange={(e) => setForm({ ...form, ical_vrbo: e.target.value })}
                placeholder="http://www.vrbo.com/icalendar/..."
                data-testid="property-vrbo-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Guest check-out time</Label>
                <Input
                  type="time"
                  value={form.checkout_time}
                  onChange={(e) => setForm({ ...form, checkout_time: e.target.value })}
                  data-testid="property-checkout-time-input"
                />
                <p className="text-[11px] text-muted-foreground">When cleaning can start</p>
              </div>
              <div className="space-y-2">
                <Label>Guest check-in time</Label>
                <Input
                  type="time"
                  value={form.checkin_time}
                  onChange={(e) => setForm({ ...form, checkin_time: e.target.value })}
                  data-testid="property-checkin-time-input"
                />
                <p className="text-[11px] text-muted-foreground">Cleaning deadline</p>
              </div>
            </div>

            {/* Door codes */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <KeyRound className="h-4 w-4" /> Door codes &amp; access
              </Label>
              <div className="space-y-1.5">
                {form.door_codes.map((c, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between bg-muted rounded-lg px-3 py-2 text-sm"
                  >
                    <span>
                      <b>{c.label}:</b> {c.code}
                    </span>
                    <button
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          door_codes: f.door_codes.filter((_, idx) => idx !== i),
                        }))
                      }
                    >
                      <X className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Label (Front door)"
                  value={newCode.label}
                  onChange={(e) => setNewCode({ ...newCode, label: e.target.value })}
                />
                <Input
                  placeholder="Code"
                  value={newCode.code}
                  onChange={(e) => setNewCode({ ...newCode, code: e.target.value })}
                />
                <Button type="button" variant="secondary" onClick={addCode}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Photo checklist */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Camera className="h-4 w-4" /> After-service photo checklist
              </Label>
              <div className="flex flex-wrap gap-1.5">
                {form.photo_checklist.map((p, i) => (
                  <Badge key={i} variant="secondary" className="gap-1">
                    {p}
                    <button
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          photo_checklist: f.photo_checklist.filter((_, idx) => idx !== i),
                        }))
                      }
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="e.g. Kitchen, Bathroom, Beds made"
                  value={newPhoto}
                  onChange={(e) => setNewPhoto(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addPhoto())}
                />
                <Button type="button" variant="secondary" onClick={addPhoto}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Access notes</Label>
              <Textarea
                value={form.access_notes}
                onChange={(e) => setForm({ ...form, access_notes: e.target.value })}
                placeholder="Parking instructions, alarm details, where supplies are kept..."
                rows={3}
              />
            </div>

            {/* Reference photos by room (Premium) */}
            <div className="space-y-2 pt-2 border-t border-border">
              <Label className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4" /> Reference photos by room
                <Badge variant="secondary" className="gap-1 text-[10px]">
                  <Sparkles className="h-3 w-3" /> Premium
                </Badge>
              </Label>
              <p className="text-[11px] text-muted-foreground">
                Show cleaners how each room should look once it's done. These appear in the
                cleaner's job view.
              </p>

              {!isPremium ? (
                <button
                  type="button"
                  onClick={() => setShowUpgrade(true)}
                  className="w-full rounded-lg border border-dashed border-border py-4 text-sm text-muted-foreground hover:bg-muted transition-colors"
                  data-testid="reference-photos-upgrade"
                >
                  Upgrade to Premium to add room reference photos
                </button>
              ) : !editingId ? (
                <p className="text-xs text-muted-foreground rounded-lg bg-muted px-3 py-3">
                  Save the property first, then re-open it to add reference photos.
                </p>
              ) : (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Room name (e.g. Living Room)"
                      value={roomLabel}
                      onChange={(e) => setRoomLabel(e.target.value)}
                      data-testid="reference-room-input"
                    />
                    <input
                      ref={refFileInput}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        uploadRefPhoto(e.target.files[0]);
                        e.target.value = "";
                      }}
                      data-testid="reference-file-input"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => refFileInput.current?.click()}
                      disabled={refUploading}
                      data-testid="reference-upload-button"
                    >
                      {refUploading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4" />
                      )}
                    </Button>
                  </div>

                  {refPhotos.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No reference photos yet.</p>
                  ) : (
                    Object.entries(
                      refPhotos.reduce((acc, p) => {
                        (acc[p.room || "General"] ||= []).push(p);
                        return acc;
                      }, {})
                    ).map(([room, photos]) => (
                      <div key={room} className="space-y-1.5">
                        <p className="text-xs font-medium text-foreground">{room}</p>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                          {photos.map((p) => (
                            <div key={p.id} className="relative group">
                              <div
                                className="aspect-square rounded-lg bg-cover bg-center bg-muted border border-border"
                                style={{ backgroundImage: `url('${photoUrl(p.storage_path)}')` }}
                              />
                              <button
                                type="button"
                                onClick={() => deleteRefPhoto(p.id)}
                                className="absolute -top-1.5 -right-1.5 bg-destructive text-white rounded-full p-0.5 shadow"
                                data-testid={`delete-reference-${p.id}`}
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving} data-testid="save-property-button">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingId ? "Save changes" : "Add property"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this property?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.name}" and its synced reservations will be permanently removed.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-property">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => remove(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-property"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <UpgradeDialog
        open={showUpgrade}
        onOpenChange={setShowUpgrade}
        feature="Reference photos by room"
      />
    </div>
  );
}
